// Export: clean HTML from the canvas, without any trace of the editor, and formatted for people.
import { getBlock } from './registry.js';
import { hasTextualChildren } from '../blocks/basic.js';

const EDITOR_ATTRIBUTES = ['data-pf', 'data-pf-edit', 'data-pf-wrapper', 'contenteditable', 'spellcheck', 'data-pf-placeholder', 'data-pf-empty'];

/** style="", which the browser leaves once an inline style is removed (an alignment undone, for instance). */
export function emptyStyle(attr) {
    return attr.name === 'style' && !attr.value.trim();
}

/**
 * A text block is written without its <div class="pflow-editor"> wrapper when it carries nothing else
 * and when the import would find the same block again (never right after another unwrapped text).
 */
function unwrapTextBlocks(root) {
    const parents = new Set(Array.from(root.querySelectorAll('[data-pf="text"]')).map(el => el.parentNode));
    for (const parent of parents) {
        let previousUnwrapped = false;
        for (const child of Array.from(parent.children)) {
            const isText = child.getAttribute('data-pf') === 'text';
            const plain = isText
                && child.localName === 'div'
                && (child.getAttribute('class') || '').trim() === 'pflow-editor'
                && Array.from(child.attributes).every(attr => attr.name === 'class' || EDITOR_ATTRIBUTES.includes(attr.name) || emptyStyle(attr))
                && hasTextualChildren(child);
            if (plain && !previousUnwrapped) {
                child.replaceWith(...child.childNodes);
                previousUnwrapped = true;
            } else {
                previousUnwrapped = false;
            }
        }
    }
}

/** Returns a clean copy of the canvas root. */
export function cleanClone(root, ctx, { markers = true } = {}) {
    const clone = root.cloneNode(true);
    // Each block cleans itself, the deepest first so that a parent sees its final children.
    for (const el of Array.from(clone.querySelectorAll('[data-pf]')).reverse()) {
        const def = getBlock(el.getAttribute('data-pf'));
        const inner = el.hasAttribute('data-pf-wrapper') ? el.firstElementChild : el;
        if (def && typeof def.exportNode === 'function' && inner) def.exportNode(inner, ctx);
    }
    unwrapTextBlocks(clone);
    clone.querySelectorAll('[data-pf-wrapper]').forEach(wrapper => wrapper.replaceWith(...wrapper.childNodes));
    clone.querySelectorAll('*').forEach(el => {
        EDITOR_ATTRIBUTES.forEach(name => el.removeAttribute(name));
        for (const attr of Array.from(el.attributes)) if (attr.name.startsWith('data-pf-')) el.removeAttribute(attr.name);
        if (el.getAttribute('class') === '') el.removeAttribute('class');
        if (el.getAttribute('style') === '') el.removeAttribute('style');
    });
    if (!markers) dropMarkers(clone);
    return clone;
}

/**
 * Removes the pflow-* classes PageFlow uses to recognize its blocks again (for a page that will not be
 * edited any more): text wrappers disappear, other elements keep their own classes.
 */
function dropMarkers(clone) {
    clone.querySelectorAll('div.pflow-editor').forEach(div => {
        if (div.attributes.length === 1 && div.getAttribute('class').trim() === 'pflow-editor') div.replaceWith(...div.childNodes);
    });
    clone.querySelectorAll('[class*="pflow-"]').forEach(el => {
        const kept = Array.from(el.classList).filter(name => !name.startsWith('pflow-'));
        if (kept.length) el.setAttribute('class', kept.join(' '));
        else el.removeAttribute('class');
    });
}

export function exportHtml(root, ctx, { format = true, markers = true } = {}) {
    const clone = cleanClone(root, ctx, { markers });
    return format ? formatHtml(clone) : clone.innerHTML;
}

// ---- Formatter ----------------------------------------------------------------------------------------

const BLOCK_TAGS = new Set([
    'address', 'article', 'aside', 'blockquote', 'details', 'dd', 'div', 'dl', 'dt', 'fieldset', 'figcaption', 'figure',
    'footer', 'form', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'header', 'hr', 'li', 'main', 'nav', 'ol', 'p', 'section',
    'summary', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr', 'ul', 'video', 'audio', 'picture', 'iframe', 'caption',
    'colgroup', 'col', 'source', 'track', 'canvas', 'menu', 'search', 'hgroup',
]);
const VERBATIM_TAGS = new Set(['pre', 'textarea', 'script', 'style', 'svg', 'math']);
const VOID_TAGS = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'source', 'track', 'wbr']);

function startTag(el) {
    const shallow = el.cloneNode(false);
    const html = shallow.outerHTML;
    return VOID_TAGS.has(el.localName) ? html : html.slice(0, html.length - `</${el.localName}>`.length);
}

function hasBlockChild(el) {
    return Array.from(el.children).some(child => BLOCK_TAGS.has(child.localName) || VERBATIM_TAGS.has(child.localName) && child.localName === 'pre');
}

/** Indents block elements; inline content and <pre> keep their exact whitespace. */
export function formatHtml(container, indent = '    ') {
    const lines = [];
    const walk = (node, depth) => {
        const pad = indent.repeat(depth);
        if (node.nodeType === 3) {
            const text = escapeText(node).replace(/\s+/g, ' ').trim();
            if (text) lines.push(pad + text);
            return;
        }
        if (node.nodeType !== 1) return;
        const tag = node.localName;
        if (VERBATIM_TAGS.has(tag) || !BLOCK_TAGS.has(tag) || !hasBlockChild(node)) {
            if (VOID_TAGS.has(tag)) {
                lines.push(pad + startTag(node));
                return;
            }
            if (VERBATIM_TAGS.has(tag)) {
                lines.push(pad + node.outerHTML);
                return;
            }
            lines.push(pad + startTag(node) + node.innerHTML.replace(/\s*\n\s*/g, ' ').trim() + `</${tag}>`);
            return;
        }
        lines.push(pad + startTag(node));
        // Inline siblings between blocks are kept together on one line.
        let inline = [];
        const flushInline = () => {
            const html = inline.map(n => (n.nodeType === 3 ? escapeText(n) : n.outerHTML)).join('').replace(/\s*\n\s*/g, ' ').trim();
            if (html) lines.push(indent.repeat(depth + 1) + html);
            inline = [];
        };
        for (const child of node.childNodes) {
            if (child.nodeType === 1 && (BLOCK_TAGS.has(child.localName) || child.localName === 'pre')) {
                flushInline();
                walk(child, depth + 1);
            } else if (child.nodeType === 1 || child.nodeType === 3) {
                inline.push(child);
            }
        }
        flushInline();
        lines.push(pad + `</${tag}>`);
    };
    let inline = [];
    const flushTop = () => {
        const html = inline.map(n => (n.nodeType === 3 ? escapeText(n) : n.outerHTML)).join('').trim();
        if (html) lines.push(html);
        inline = [];
    };
    for (const child of container.childNodes) {
        if (child.nodeType === 1 && (BLOCK_TAGS.has(child.localName) || child.localName === 'pre')) {
            flushTop();
            walk(child, 0);
        } else if (child.nodeType === 1 || child.nodeType === 3) inline.push(child);
    }
    flushTop();
    return lines.join('\n');
}

function escapeText(node) {
    const div = node.ownerDocument.createElement('div');
    div.append(node.ownerDocument.createTextNode(node.textContent));
    return div.innerHTML;
}
